import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { NavLink } from 'react-router-dom'
import { OrdersFormViewModel, OrderViewModel } from '@ail/orders-api'
import { printDate } from '../../../helpers/Utils'

export function RequestDetails(props: { orders: OrderViewModel[]; forms: OrdersFormViewModel[] }) {
  const [searchKeyword, setSearchKeyword] = useState<string>('')
  const [selectedYear, setSelectedYear] = useState<string>('all')
  const [hiddenForms, setHiddenForms] = useState<string[]>([])

  const { t, i18n } = useTranslation('common')

  const filteredOrders = useMemo(() => {
    return props.orders.filter((o) => {
      const date = new Date(o.modified)
      const keywordMatch =
        !searchKeyword ||
        o.userDisplayName.toLowerCase().includes(searchKeyword) ||
        o.id.toLowerCase().includes(searchKeyword)
      const yearMatch = selectedYear === 'all' || date.getFullYear().toString() === selectedYear
      const formsMatch = !hiddenForms.includes(o.formId)
      return keywordMatch && yearMatch && formsMatch
    })
  }, [hiddenForms, props.orders, searchKeyword, selectedYear])

  const uniqueYears = useMemo(() => {
    const result: string[] = []
    for (const order of props.orders) {
      const year = new Date(order.modified).getFullYear().toString()
      if (!result.includes(year)) {
        result.push(year)
      }
    }
    return result.toSorted((a, b) => b.localeCompare(a))
  }, [props.orders])

  const toggleFormHidden = (id: string) => {
    if (hiddenForms.includes(id)) {
      setHiddenForms(hiddenForms.filter((f) => f !== id))
    } else {
      setHiddenForms([...hiddenForms, id])
    }
  }
  const orderClassName = (order: OrderViewModel) => {
    if (!order?.approvalProcess) {
      if (order.internalOrderStatus === 'InProgress' || order.internalOrderStatus === 'OnHold') {
        return 'pending'
      }
      if (order.internalOrderStatus === 'Done') {
        return 'checked'
      }
      if (order.internalOrderStatus === 'Cancelled') {
        return 'denied'
      }
    }

    if (order.approvalProcess?.status === 'Approved') {
      return 'checked'
    }
    if (order.approvalProcess?.status === 'Declined') {
      return 'denied'
    }
    if (order.approvalProcess?.status === null) {
      return null
    }
    if (order.approvalProcess?.status === 'Pending') {
      return 'pending'
    }
  }

  const getFormIcon = (order: OrderViewModel) => {
    return props.forms.find((f) => f.id === order.formId)?.icon ?? 'icon-order'
  }

  const getFormTitle = (order: OrderViewModel) => {
    const form = props.forms.find((f) => f.id === order.formId)
    return form?.tr?.[i18n.language] ?? form?.name ?? ''
  }

  return (
    <div className="detailContentArea eyeOk">
      <div className="detailTitle docTitleArea relative eyeOk" style={{ flexWrap: 'wrap', paddingRight: '5px' }}>
        <div className="titleDoc">
          <span>
            <i className="icon-requests"></i>
            {t('REQUESTS')}
          </span>
          <div className="toolArea">
            {
              <div className="input-area">
                <input
                  value={searchKeyword}
                  onChange={(e) => setSearchKeyword(e.target.value.toLowerCase())}
                  type="search"
                  className="transparent"
                ></input>
                {!!searchKeyword && <i className="icon-clear" onClick={() => setSearchKeyword('')} />}
                <i className="icon-search" />
              </div>
            }
          </div>
        </div>
      </div>
      <div className="toggle-grouping-wrap">
        <div className="selectArea">
          <select onChange={(e) => setSelectedYear(e.target.value)} className="filterByYear">
            <option value="all" selected={selectedYear === 'all'} className="filterByYearOptions">
              {t('ALL')}
            </option>
            {uniqueYears.map((el) => (
              <option value={el} selected={selectedYear === el} className="filterByYearOptions">
                {el}
              </option>
            ))}
          </select>
          <i className="icon-bottom"></i>
        </div>
        <div className="groups-toggle">
          {props.forms.map((el) => (
            <div
              onClick={() => toggleFormHidden(el.id)}
              title={el.tr?.[i18n.language] ?? el.name}
              className="group-toggle-row"
            >
              <i className={`icon-check ${hiddenForms.includes(el.id) ? 'inactive' : 'active'}`}></i>
              <i className={el.icon}></i>
            </div>
          ))}
        </div>
      </div>
      <div className="details docDetails requests">
        <div className="docs-wrap">
          <div className="p-datatable p-component">
            <div className="p-datatable-wrapper">
              <table role="grid" style={{ tableLayout: 'auto' }}>
                <thead className="p-datatable-thead">
                  <tr>
                    <th className="description-column">
                      <span className="p-column-title">{t('TYPE')}</span>
                    </th>
                    <th className="">
                      <span className="p-column-title">{t('MODIFIED')}</span>
                    </th>
                    <th className="">
                      <span className="p-column-title">{t('OWNER')}</span>
                    </th>
                    <th className="">
                      <span className="p-column-title">{t('INTERNAL_REFERENCE')}</span>
                    </th>
                    <th className="status" colSpan={2}>
                      <span className="p-column-title">{t('STATUS')}</span>
                    </th>
                  </tr>
                </thead>
                <tbody className="p-datatable-tbody">
                  {filteredOrders.length
                    ? filteredOrders.map((el) => (
                        <tr className="p-datatable-row">
                          <td className="description-column">
                            <i className={`${getFormIcon(el)}`}></i>
                            <span className="tooltipText">{getFormTitle(el)}</span>
                          </td>
                          <td className="">{printDate(new Date(el.modified))}</td>
                          <td>
                            <div className="ellipsis">{el.userDisplayName}</div>
                          </td>
                          <td className="">
                            <div className="ellipsis">{el.data['internalReference'] || ''}</div>
                          </td>
                          <td className={`status ${el?.approvalProcess ? orderClassName(el) : null}`}>
                            {!!el.internalOrderStatus &&
                            el.internalOrderStatus !== 'New' &&
                            !el.approvalProcess?.status ? (
                              <span>{t(el.internalOrderStatus.toUpperCase())}</span>
                            ) : (
                              <span>
                                {el.approvalProcess
                                  ? (el.approvalProcess.status === 'Approved' ||
                                      el.approvalProcess.status === 'Pending') &&
                                    t(el.approvalProcess.status.toUpperCase())
                                  : t('DRAFT')}
                              </span>
                            )}
                          </td>
                          <td className="download-column">
                            <NavLink className="icon-show" to={`/requests/${el.id}`}></NavLink>
                          </td>
                        </tr>
                      ))
                    : !!searchKeyword && (
                        <tr className="p-datatable-row">
                          <td></td>
                          <td></td>
                          <td>{t('NO_RESULTS_FOUND')}</td>
                          <td></td>
                          <td></td>
                        </tr>
                      )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
